import React, { useEffect, useState } from 'react';
import {
  Alert, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, MenuItem, Stack, Switch, TextField, Typography,
} from '@mui/material';

const EMPTY = {
  name: '', role: '', instructions: '', reports_to_bot_id: '', permissions: [], active: true,
};

/** Create or edit a team member. `bot` null means create. Access starts empty and is chosen explicitly. */
export default function BotFormDialog({
  open, bot, bots, catalog, saving, error, onClose, onSave,
}) {
  const [form, setForm] = useState(EMPTY);
  const [localError, setLocalError] = useState('');

  useEffect(() => {
    if (!open) return;
    setLocalError('');
    if (bot) {
      setForm({
        name: bot.name,
        role: bot.role,
        instructions: bot.instructions,
        reports_to_bot_id: bot.reports_to_bot_id || '',
        permissions: bot.permissions,
        active: bot.active,
      });
    } else {
      const defaultBoss = bots.find((b) => b.kind === 'coordinator' && b.active) || bots.find((b) => b.is_primary);
      setForm({ ...EMPTY, reports_to_bot_id: defaultBoss?.id || '' });
    }
  }, [open, bot, bots]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const toggle = (action) => setForm((f) => ({
    ...f, permissions: f.permissions.includes(action) ? f.permissions.filter((a) => a !== action) : [...f.permissions, action],
  }));

  const submit = (e) => {
    e.preventDefault();
    if (form.name.trim().length < 2) { setLocalError('Give the team member a name (at least 2 characters).'); return; }
    if (form.role.trim().length < 2) { setLocalError('Add a short role, for example "Sales".'); return; }
    if (form.instructions.trim().length < 10) { setLocalError('Describe what they do (at least 10 characters).'); return; }
    if (!bot && !form.reports_to_bot_id) { setLocalError('Choose who they report to.'); return; }
    setLocalError('');
    onSave(bot ? form : { ...form, active: undefined });
  };

  const managers = bots.filter((b) => b.active && (!bot || b.id !== bot.id));

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth component="form" onSubmit={submit}>
      <DialogTitle>{bot ? `Edit ${bot.name}` : 'Add a team member'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {(localError || error) && <Alert severity="error">{localError || error}</Alert>}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField label="Name" value={form.name} onChange={set('name')} fullWidth autoFocus inputProps={{ maxLength: 60 }} />
            <TextField label="Role" value={form.role} onChange={set('role')} fullWidth inputProps={{ maxLength: 60 }} />
          </Stack>
          <TextField select label="Reports to" value={form.reports_to_bot_id} onChange={set('reports_to_bot_id')} fullWidth>
            {bot && <MenuItem value="">Unassigned (outside the chart)</MenuItem>}
            {managers.map((b) => <MenuItem key={b.id} value={b.id}>{`${b.name} (${b.role})`}</MenuItem>)}
          </TextField>
          <TextField
            label="What they do and how they behave"
            value={form.instructions}
            onChange={set('instructions')}
            multiline
            minRows={4}
            maxRows={10}
            fullWidth
            helperText={`${form.instructions.length}/4000`}
            inputProps={{ maxLength: 4000 }}
          />
          <div>
            <Typography fontWeight={600}>Access</Typography>
            <Typography variant="body2" color="text.secondary">Team members only get what you tick here.</Typography>
            <Stack>
              {catalog.map((c) => (
                <FormControlLabel
                  key={c.action}
                  control={<Checkbox size="small" checked={form.permissions.includes(c.action)} onChange={() => toggle(c.action)} />}
                  label={(
                    <div>
                      <Typography variant="body2">{c.label}</Typography>
                      <Typography variant="caption" color="text.secondary">{c.description}</Typography>
                    </div>
                  )}
                />
              ))}
            </Stack>
          </div>
          {bot && (
            <FormControlLabel
              control={<Switch checked={form.active} onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))} />}
              label="Active (inactive members do not take part)"
            />
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button type="submit" variant="contained" disabled={saving}>{bot ? 'Save' : 'Add team member'}</Button>
      </DialogActions>
    </Dialog>
  );
}
